import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnChanges,
  OnInit,
  AfterViewInit
} from "@angular/core";
import { Subscription, fromEvent, debounceTime, take, BehaviorSubject, startWith } from "rxjs";
import { CdrSummaryStatisticPhoneService } from "@onsip/common/services/api/resources/cdrSummaryStatistic/cdr-summary-statistics-phone.service";
import { CdrSummaryStatisticPhone } from "@onsip/common/services/api/resources/cdrSummaryStatistic/cdr-summary-statistic";
import { BarGraphData } from "../charts/barGraph/bar-graph.component";
import { DonutGraphData } from "../charts/donutGraph/donut-graph.component";
import { DidService } from "@onsip/common/services/api/resources/did/did.service";
import { Did } from "@onsip/common/services/api/resources/did/did.service";
import { FormatE164Pipe } from "@onsip/web/features/shared/pipes/format-e164.pipe";
import { SnackbarService } from "@onsip/web/features/shared/components/snackbar/snackbar.service";

interface CachedResult {
  inboundData: Array<BarGraphData>;
  phoneNumberData: Array<DonutGraphData>;
}

@Component({
  selector: "onsip-pbx-inbound-calling-activity",
  templateUrl: "./inbound-calling-activity.component.html",
  styleUrls: ["./inbound-calling-activity.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [FormatE164Pipe],
  standalone: false
})
export class InboundCallingActivityComponent
  implements OnInit, OnDestroy, OnChanges, AfterViewInit
{
  @Input() dateStart!: string;
  @Input() dateEnd!: string;

  inboundData: Array<BarGraphData> = [];
  phoneNumberData: Array<DonutGraphData> = [];
  numberNameMap: Map<string, string> = new Map<string, string>();
  cachedResult: Record<string, CachedResult> = {};
  dateRange = this.dateStart + "-" + this.dateEnd;
  barGraphWidth = 0;
  loadingStateComplete$ = new BehaviorSubject(true);

  constructor(
    private cdrPhoneService: CdrSummaryStatisticPhoneService,
    private cdRef: ChangeDetectorRef,
    private didService: DidService,
    private formatE164: FormatE164Pipe,
    private snackbar: SnackbarService,
    private hostElement: ElementRef
  ) {}

  private unsubscriber = new Subscription();

  ngOnInit() {
    this.browseAndInit();
  }

  ngAfterViewInit(): void {
    this.handleWindowResize();
  }

  ngOnChanges(): void {
    this.initCharts();
  }

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }

  private async browseAndInit(): Promise<void> {
    await this.didService.didBrowse();
    this.initCharts();
  }

  private initCharts(): void {
    this.dateRange = this.dateStart + "-" + this.dateEnd;
    if (this.cachedResult[this.dateRange]) {
      this.inboundData = this.cachedResult[this.dateRange].inboundData;
      this.phoneNumberData = this.cachedResult[this.dateRange].phoneNumberData;
      return;
    }
    this.loadingStateComplete$.next(false);
    this.getActiveInactiveNumbers(this.dateStart, this.dateEnd);
  }

  private buildPhoneNameMap(dids: Array<Did>): void {
    dids.map(did => {
      this.numberNameMap.set(
        did.number,
        did.userNumberAlias ? did.userNumberAlias.addressName : "Unknown"
      );
    });
  }

  private getInboundActivity(inboundPhoneCdrs: Array<CdrSummaryStatisticPhone>): void {
    // go through inbound phone number cdr objects and tally number of inbound calls by userId
    const phoneNumberInboundCountHash: { [label: string]: number } = {};
    inboundPhoneCdrs.map(phoneCdr => {
      if (phoneCdr.e164 in phoneNumberInboundCountHash) {
        phoneNumberInboundCountHash[phoneCdr.e164] += Number(phoneCdr.origination);
      } else {
        phoneNumberInboundCountHash[phoneCdr.e164] = Number(phoneCdr.origination);
      }
    });
    this.inboundData = Object.entries(phoneNumberInboundCountHash).map(([key, val]) => {
      return {
        name: this.numberNameMap.get(key) || key,
        value: val,
        tooltipInfo: this.formatE164.transform(key)
      };
    });
    this.cachedResult[this.dateRange].inboundData = this.inboundData;
  }

  private getActiveInactiveNumbers(startDate: string, endDate: string): void {
    this.unsubscriber.add(
      this.didService.orgState.pipe(take(1)).subscribe(dids => {
        this.buildPhoneNameMap(dids);
        const numOfPhoneNumbers = dids.length;
        this.cdrPhoneService
          .cdrSummaryStatisticPhone({ StartDateTime: startDate, EndDateTime: endDate })
          .then(res => {
            if (res.status === "success") {
              // filter cdrs are that are only inbound and in the same PBX
              const inboundPhoneCdrs = Object.values(res.data)
                .filter(phone => Number(phone.origination) > 0)
                .sort((a, b) => Number(b.origination) - Number(a.origination));
              const numOfActive = inboundPhoneCdrs.length;
              const numOfInactive = numOfPhoneNumbers - numOfActive;
              this.phoneNumberData = [
                { name: "active", value: numOfActive },
                { name: "inactive", value: numOfInactive }
              ];

              this.cachedResult[this.dateRange] = {
                inboundData: [],
                phoneNumberData: []
              } as CachedResult;
              this.cachedResult[this.dateRange].phoneNumberData = this.phoneNumberData;
              this.getInboundActivity(inboundPhoneCdrs);
              this.cdRef.markForCheck();
            } else {
              this.snackbar.openSnackBar(res.data.message, "error");
              this.cdrPhoneService.clearErrors();
            }
            this.loadingStateComplete$.next(true);
          });
      })
    );
  }

  private handleWindowResize(): void {
    // adjust width of the bar graph depending on the current window size if that changes
    this.unsubscriber.add(
      fromEvent(window, "resize")
        .pipe(startWith(true), debounceTime(500))
        .subscribe(() => {
          this.adjustBarGraphWidth();
          this.cdRef.markForCheck();
        })
    );
  }

  adjustBarGraphWidth(): void {
    this.barGraphWidth = this.hostElement.nativeElement.offsetWidth;
    if (window.innerWidth >= 600) {
      // remove width space for the donut graph
      this.barGraphWidth -= 155;
    }
  }
}
